import { Checkbox } from '@mui/material';
import React from 'react';

import Fg677Icon from '../../../assets/icons/677fg.svg';
import IgreeksIcon from '../../../assets/icons/igreeks-white.svg';
import { BLUR_STYLE } from '../../../providers/demo-blur';
import { MultiSelectListAccounts } from '../../../components/multi-select/multi-select-list-accounts';
import Utils from '../../../utils/immersive_utils';
import './styles.scss';
import { IAccountOption } from '../../../providers/venue-provider';

interface AggregatedOTCAccountListProps {
  exchange: string;
  label: string;
  icon: any;
  credentials: any[];
  isPortfolioSidebarOpen: boolean;
  selectedAccountsAggregated: IAccountOption[];
  setSelectedAccountsAggregated: (items: IAccountOption[]) => void;
  // Opt-in "blur names for demo" (Portfolio Management/Performance). Off by default.
  isBlurred?: boolean;
}

export const AggregatedOTCAccountList: React.FC<AggregatedOTCAccountListProps> = ({
  exchange,
  label,
  icon,
  credentials,
  isPortfolioSidebarOpen,
  selectedAccountsAggregated,
  setSelectedAccountsAggregated,
  isBlurred = false,
}) => {
  const filteredCredentials = credentials.filter((acc) => acc.exchange === exchange);

  const allItems: IAccountOption[] = filteredCredentials.flatMap((credential) =>
    credential.items.map((item: any) => ({
      name: item.name,
      exchange: item.exchange,
      venue: item.venue || '',
    })),
  );

  const groupedByVenue = allItems.reduce((acc: Record<string, IAccountOption[]>, item) => {
    const venueKey = item.venue || 'Outros';
    if (!acc[venueKey]) acc[venueKey] = [];
    acc[venueKey].push(item);
    return acc;
  }, {});

  const hasNonOtcSelected = selectedAccountsAggregated.some((sel) => sel.exchange !== 'otc');
  const disableSelection = hasNonOtcSelected;

  const isAllSelected = (items: IAccountOption[]) =>
    items.every((item) =>
      selectedAccountsAggregated.some(
        (sel) => sel.name === item.name && sel.exchange === item.exchange,
      ),
    );

  const handleSelectAllChangeForGroup = (items: IAccountOption[], isChecked: boolean) => {
    if (disableSelection) return;
    if (isChecked) {
      const toAdd = items.filter(
        (item) =>
          !selectedAccountsAggregated.some(
            (sel) => sel.name === item.name && sel.exchange === item.exchange,
          ),
      );
      setSelectedAccountsAggregated([...selectedAccountsAggregated, ...toAdd]);
    } else {
      const toKeep = selectedAccountsAggregated.filter(
        (sel) => !items.some((item) => sel.name === item.name && sel.exchange === item.exchange),
      );
      setSelectedAccountsAggregated(toKeep);
    }
  };

  const handleOptionChange = (newItems: IAccountOption[]) => {
    if (disableSelection) return;
    setSelectedAccountsAggregated(newItems);
  };

  return (
    <div className="portfolio-accounts-aggregated-venue">
      {allItems.length > 0 && (
        <div className="portfolio-account-aggregated-label">
          <img src={icon} alt={exchange} />
          <span>{label}</span>
        </div>
      )}
      {Object.entries(groupedByVenue).map(([venue, items]) => {
        const groupAllSelected = isAllSelected(items);

        return (
          <div key={venue} className="aggregated-otc-group" style={{ marginLeft: '1rem' }}>
            <div
              className={`aggregated-otc-group-label ${disableSelection ? 'disabled' : ''}`}
              onClick={() => handleSelectAllChangeForGroup(items, !groupAllSelected)}
            >
              <Checkbox
                checked={groupAllSelected}
                onChange={(e) => handleSelectAllChangeForGroup(items, e.target.checked)}
                disabled={disableSelection}
              />
              <div className="otc-venue-title-area">
                <img src={venue === 'igreeks' ? IgreeksIcon : Fg677Icon} alt={venue} />
                <span style={isBlurred ? BLUR_STYLE : undefined}>{Utils.truncateLabel(venue)}</span>
              </div>
            </div>
            <div
              className={`portfolio-otc-accounts-multi-select-list ${
                isPortfolioSidebarOpen ? 'portfolio-accounts-multi-select-hover' : ''
              }`}
            >
              <MultiSelectListAccounts
                items={items}
                selectedItems={selectedAccountsAggregated}
                setSelectedItems={handleOptionChange}
                multiSelectDisabled={disableSelection}
                isBlurred={isBlurred}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};
